import React, { useEffect, useRef, useState } from "react"
import Select from "react-select"
import ChartCard from "./ChartCard"
import { Button } from "reactstrap"
import { customStyles } from "utils/SelectStyles"
import { FaFilter } from "react-icons/fa"
import Breadcrumb from "components/Common/Breadcrumb"
import { FetchPostData } from "utils/Common_api_functions"
import Loader from "components/Loader/loader"
import { DatePicker } from "antd"
import { months } from "utils/defaultpositions"
import secureLocalStorage from "react-secure-storage"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import Swal from "sweetalert2"
import { MdDownload } from "react-icons/md"
import dayjs from "dayjs"

const { RangePicker } = DatePicker

const currentYear = new Date().getFullYear()
const defaultYear = [{ label: `${currentYear}`, value: currentYear }]
const years = Array.from({ length: currentYear - 2020 }, (_, i) => ({
  label: `${2021 + i}`,
  value: 2021 + i,
}))?.reverse()

const GMP = () => {
  const [loading, setLoading] = useState(false)
  const [GMPData, setGMPData] = useState({})
  const [dateRange, setDateRange] = useState([])
  const [selectedMonths, setSelectedMonths] = useState([])
  const [selectedYears, setSelectedYears] = useState(defaultYear)

  const [sortAccidents, setSortAccidents] = useState("chronological")
  const [sortAbsence, setSortAbsence] = useState("chronological")
  const [sortComplaints, setSortComplaints] = useState("chronological")
  const [sortCCPs, setSortCCPs] = useState("chronological")

  const [currentweek, setCurrentWeek] = useState([])
  const [maxDate, setMaxDate] = useState(null)

  useEffect(() => {
    // Only pull the "latest week uploaded" info for the badge display.
    // Do NOT pre-fill the actual filter inputs (Date Range / Month / Year).
    const FinalRangeRaw = secureLocalStorage?.getItem("FINALRANGE")

    const latestWeek = FinalRangeRaw ? FinalRangeRaw.map(d => dayjs(d)) : []
    setCurrentWeek(latestWeek)
    setMaxDate(FinalRangeRaw?.[1] ? dayjs(FinalRangeRaw[1]) : null)

    // Date Range & Month start empty — user must choose and apply them manually.
    // Year stays defaulted to the current year (2026).
    setSelectedYears(defaultYear)
    setSelectedMonths([])
    setDateRange([])

    const fetchInitialData = async () => {
      const APidata = await FetchPostData(setLoading, "sheet/gmp", {
        year: defaultYear?.map(item => item?.value)?.join(","),
      })
      if (APidata?.data?.success) setGMPData(APidata?.data)
      else {
        setGMPData({})
        console.log(APidata?.response)
      }
    }

    fetchInitialData()
  }, [])

  const handleFilter = async () => {
    const APidata = await FetchPostData(setLoading, "sheet/gmp", {
      startDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      endDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      month: selectedMonths?.map(item => item?.value)?.join(","),
      year: selectedYears?.map(item => item?.value)?.join(","),
    })
    if (APidata?.data?.success) setGMPData(APidata?.data)
    else {
      setGMPData({})
      console.log(APidata?.response)
    }
  }

  const dashboardRef = useRef(null)

  const handleExport = async () => {
    const element = dashboardRef.current
    if (!element) return

    try {
      setLoading(true)

      const originalHeight = element.style.height
      const originalOverflow = element.style.overflow
      element.style.height = "auto"
      element.style.overflow = "visible"

      // ✅ Get the element's actual position on the page
      const rect = element.getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const elementTop = rect.top + scrollTop // absolute top position of dashboardRef

      const canvas = await html2canvas(element, {
        scale: 1.2,
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: -scrollTop, // ✅ This corrects the scroll offset
        y: 0, // ✅ Start capturing from top of the element itself
        x: 0,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        logging: false,
      })

      element.style.height = originalHeight
      element.style.overflow = originalOverflow

      const imgData = canvas.toDataURL("image/jpeg", 0.75)

      const imgWidth = canvas.width / 1.2
      const imgHeight = canvas.height / 1.2

      const marginLeft = 30
      const marginRight = 30
      const marginTop = 10
      const marginBottom = 20

      const pdfWidth = imgWidth + marginLeft + marginRight
      const pdfHeight = imgHeight + marginTop + marginBottom

      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? "landscape" : "portrait",
        unit: "px",
        format: [pdfWidth, pdfHeight],
        compress: true,
      })

      pdf.addImage(
        imgData,
        "JPEG",
        marginLeft,
        marginTop,
        imgWidth,
        imgHeight,
        undefined,
        "FAST"
      )

      pdf.save(
        `GMP_Charts_${new Date()
          .toLocaleDateString("en-GB")
          .replace(/\//g, "-")}.pdf`
      )
    } catch (e) {
      Swal.fire({
        title: "Export Failed",
        text: "Something went wrong while exporting.",
        icon: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="page-content" ref={dashboardRef}>
      {loading && <Loader />}

      {/* ── Filter Header ─────────────────────────────────── */}
      <div
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)",
          borderRadius: "16px",
          border: "1px solid #ede9fe",
          boxShadow: "0 4px 24px rgba(108,99,255,0.07)",
          padding: "16px 20px",
          marginBottom: "24px",
          position: "relative",
          overflow: "visible", // ← important: don't clip dropdowns
        }}
      >
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div>
              <h4
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#000",
                }}
              >
                GMP
              </h4>
              <p style={{ margin: 0, fontSize: "13px", color: "#666" }}>
                Good Manufacturing Practice
              </p>
            </div>
            <div
              style={{ width: "1px", height: "36px", background: "#e2e8f0" }}
            />

            {/* Export button */}
            <Button
              onClick={handleExport}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "#0f172a",
                border: "none",
                borderRadius: "10px",
                padding: "8px 16px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#fff",
                cursor: "pointer",
                letterSpacing: "0.2px",
              }}
            >
              <MdDownload size={15} />
              Export
            </Button>
            {currentweek?.[0] && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "10px",
                  padding: "6px 12px",
                }}
              >
                <span
                  style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "#16a34a",
                    display: "inline-block",
                    boxShadow: "0 0 0 2px #bbf7d0",
                  }}
                />
                <div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#15803d",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                    }}
                  >
                    Latest week Upload
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#166534",
                      fontWeight: 600,
                      marginTop: "1px",
                    }}
                  >
                    {currentweek[0].format("DD MMM")} —{" "}
                    {currentweek[1].format("DD MMM YYYY")}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <RangePicker
              value={dateRange}
              placeholder={["Start (Sat)", "End (Fri)"]}
              onChange={values => setDateRange(values)}
              format="DD-MM-YYYY"
              style={{ borderRadius: "8px", height: "38px" }}
              disabledDate={current => {
                return maxDate ? current && current > maxDate.endOf("day") : false
              }}
            />
            <Select
              placeholder="Month"
              options={months}
              styles={customStyles}
              isMulti
              value={selectedMonths}
              onChange={v => {
                setSelectedMonths(v)
                if (!v || v.length === 0) {
                  setSortAccidents("chronological")
                  setSortAbsence("chronological")
                  setSortComplaints("chronological")
                  setSortCCPs("chronological")
                }
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <Select
              placeholder="Year"
              options={years}
              styles={customStyles}
              isMulti
              value={selectedYears}
              onChange={v => {
                setSelectedYears(v)
                if (!v || v.length === 0) {
                  setSortAccidents("chronological")
                  setSortAbsence("chronological")
                  setSortComplaints("chronological")
                  setSortCCPs("chronological")
                }
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <Button onClick={handleFilter} className="btn-blue rounded-2">
              <FaFilter size={12} />
            </Button>
          </div>
        </div>
      </div>

      {/* ── Chart Grid ────────────────────────────────────── */}
      <div className="row g-3">
        <div className="col-md-12">
          <ChartCard
            title="Accidents"
            data={GMPData?.accidents}
            previousData={GMPData?.accidentsPreviousYear}
            valueKey="accidents"
            sortOrder={sortAccidents}
            onSortOrderChange={setSortAccidents}
          />
        </div>
        <div className="col-md-12">
          <ChartCard
            title="Absence"
            data={GMPData?.absence}
            previousData={GMPData?.absencePreviousYear}
            valueKey="absence"
            showPercentage
            sortOrder={sortAbsence}
            onSortOrderChange={setSortAbsence}
          />
        </div>
        <div className="col-md-12">
          <ChartCard
            title="Complaints"
            data={GMPData?.complaints}
            previousData={GMPData?.complaintsPreviousYear}
            valueKey="complaints"
            sortOrder={sortComplaints}
            onSortOrderChange={setSortComplaints}
          />
        </div>
        <div className="col-md-12">
          <ChartCard
            title="CCP Issues"
            data={GMPData?.ccps}
            previousData={GMPData?.ccpsPreviousYear}
            valueKey="issues"
            sortOrder={sortCCPs}
            onSortOrderChange={setSortCCPs}
          />
        </div>
      </div>
    </div>
  )
}

export default GMP